import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import SvgOpenExternal from '../svg/OpenExternal';
import {
  unitsText,
  devicesText,
  emptyText,
} from '../shared/SharedInternationalizations';
import SvgUserSolid from '../svg/UserSolid';
import UserStatusDisplay from './UserStatusDisplay';
import UserQuickActionsMenu from './UserQuickActionsMenu';
import MediaImage from '../ui/MediaImage';

/**
 * The Card view for displaying users
 * @param {number} userInfo - The ID of the user to be displayed in the card
 * @description - The component uses connected data from Redux to cross-reference the user ID to the full user data to be displayed.
 */

export function UnconnectedUserCard(props) {
  const { user, location, unitsObj } = props;
  const currentLocation = location.pathname + location.search;
  const {
    id,
    last_name,
    suffix,
    preferred_name,
    title,
    first_name,
    phone, 
    email,
    username,
    active_unit,
    units,
    status,
    is_active,
    assigned_device_count,
    photo,
  } = user;

  let userActiveUnit = '';
  if (active_unit) {
    if (unitsObj[active_unit]) {
      userActiveUnit = (
        <Link
          data-test="active-unit-link"
          className="dropdown-menu__menu-content-item link-text"
          to={`/units/${active_unit}/view/`}
          state={{
            previous: currentLocation,
          }}>
          {unitsObj[active_unit].name}
          <SvgOpenExternal className="text-smallest margin-left-5" />
        </Link>
      );
    }
  }

  const unitsCount = units.length;

  // const groupsCount = groups.length;

  let userImage = (
    <div className="user-card__user-image">
      <SvgUserSolid data-test="user-generic-image" />
    </div>
  );
  if (photo && photo.http_path) {
    userImage = (
      <MediaImage
        data-test="user-api-image"
        alt="User profile"
        className="user-card__user-image"
        url={photo.http_path}
      />
    );
  }

  return (
    <div className="user-card" data-test="user-card">
      <div className="user-card__user-image-container" data-test="user-image">
        {userImage}
      </div>
      <div
        className={
          is_active
            ? 'user-card__container'
            : 'user-card__container user-card__container--inactive'
        }>
        <div className="absolute-top-right">
          <UserQuickActionsMenu user={user} currentLocation={currentLocation} />
        </div>
        <div className="user-card__content-container content-container--column">
          <div
            className="user-card__user-name text-extra-large  text-center"
            data-test="user-first-name">
            {title}&nbsp;{first_name}&nbsp;
            {preferred_name ? `"${preferred_name}"` : ''}
          </div>
          <div
            className="user-card__user-name text-extra-large text-bold text-center"
            data-test="user-last-name">
            &nbsp;{last_name}&nbsp;{suffix}&nbsp;
          </div>
          <div
            className="user-card__user-info  text-center"
            data-test="user-phone">
            <FormattedMessage
              id="userCard.phoneText"
              defaultMessage="Phone Number"
              description="The user phone number">
              {(txt) => <strong className="margin-right-5">{txt}:</strong>}
            </FormattedMessage>
            &nbsp;
            {phone ? phone : <span className="text-dimmed">{emptyText}</span>}
          </div>
          
        
          
          <div className="user-card__user-info text-center">
            <FormattedMessage
              id="userCard.emailText"
              defaultMessage="Email"
              description="The user phone number">
              {(txt) => <strong className="margin-right-5">{txt}:</strong>}
            </FormattedMessage>
            &nbsp;
            {email ? (
              <a
                href={`mailto:${email}`}
                className="link-text"
                data-test="user-email">
                {email}
              </a>
            ) : (
              <span className="text-dimmed">{emptyText}</span>
            )}
          </div>
            {/* Username displayed on UserCard that does not start with gen_ */}
          <div
            className="user-card__user-info  text-center"
            data-test="username">
            <FormattedMessage
              id="userCard.username"
              defaultMessage="Username"
              description="The users username">
              {(txt) => <strong className="margin-right-5">{txt}:</strong>}
            </FormattedMessage>
            &nbsp;
            {username && !username.startsWith('gen_') 
            ? username  : <span className="text-dimmed">{emptyText}</span>}
          </div>

          <div
            className="user-card__user-info margin-bottom-10 text-center"
            data-test="user-active-unit-link">
            <FormattedMessage
              id="userCard.activeUnitText"
              defaultMessage="Active Unit"
              description="The active unit for a user">
              {(txt) => <strong>{txt}:</strong>}
            </FormattedMessage>
            &nbsp;
            {active_unit ? (
              userActiveUnit
            ) : (
              <span className="text-dimmed">{emptyText}</span>
            )}
          </div>
          <div className="user-card__user-stats">
            <div className="user-card-stats__container">
              <span
                className="text-deep-blue text-extra-large text-bold "
                data-test="user-devices">
                {assigned_device_count ? assigned_device_count : 0}
              </span>
              {devicesText}
            </div>
            <div className="user-card-stats__container">
              <span
                className="text-deep-blue text-extra-large text-bold"
                data-test="user-units">
                {unitsCount}
              </span>
              {unitsText}
            </div>
          </div>
          <UserStatusDisplay
            userId={id}
            userStatus={status}
            first_name={first_name}
            last_name={last_name}
            is_active={is_active}
          />
          <div className="user-card__user-links">
            <FormattedMessage
              id="userCard.fullViewButton"
              defaultMessage="Full View"
              description="Button to view full user info on user card component"
              data-test="full-view-link-text">
              {(txt) => (
                <Link
                  data-test="full-view-link"
                  to={`/users/${id}/view/`}
                  state={{
                    previous: currentLocation,
                  }}
                  className="user-card-links__link link-text">
                  {txt} <SvgOpenExternal className="text-small margin-left-5" />
                </Link>
              )}
            </FormattedMessage>
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  const userId = ownProps.userId;
  const user = state.users.users[userId];

  return {
    user,
    unitsObj: state.units.units,
    userStatuses: state.users.userStatuses,
  };
};
export default connect(mapStateToProps)(UnconnectedUserCard);
